import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatUtc } from '../../components/Async'
import { ApiError, alerts, assistant as assistantApi, type CasePolicyDraft } from '../../lib/api'
import { describeDraft, draftHistory, draftOffer, readDraft, readDraftFailure, type DraftReading } from './draftFromCase'

/**
 * The last step of the loop: a case somebody read becomes a policy somebody reviews.
 *
 * Everything that decides anything is in `draftFromCase.ts` beside this file and is tested there.
 * What is left here is the shape and the three things it is worth reading in the markup.
 *
 * The button is hidden rather than disabled for an analyst. That is deliberate and is the opposite of
 * this console's usual habit - a disabled control is right when the operator could change the reason,
 * and an analyst cannot become an author by doing anything on this screen.
 *
 * It never publishes. What comes back is a draft, and the server has four independent guards keeping
 * it that way; the sentence under the button says so, because "the assistant wrote us a policy" is
 * exactly the thing somebody will assume went straight to the fleet.
 *
 * And the drafts a case has already produced are listed whether or not this person may add one, so a
 * second analyst can see that somebody has already dealt with this rather than asking again.
 */
export function DraftPolicyPanel({ alertId, roles }: { alertId: string; roles: readonly string[] }) {
  const [availability, setAvailability] = useState<{ available: boolean; enabled: boolean } | null>(null)
  const [existing, setExisting] = useState<CasePolicyDraft[]>([])
  const [reading, setReading] = useState<DraftReading | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let current = true

    // The settings the Assistant screen already reads, rather than a new endpoint. A failure leaves
    // availability null, which `draftOffer` treats as "not read" and renders nothing for - an unread
    // setting is not a setting.
    assistantApi
      .settings()
      .then((settings) => {
        if (current) setAvailability({ available: settings.available, enabled: settings.enabled })
      })
      .catch(() => {
        if (current) setAvailability(null)
      })

    alerts
      .drafts(alertId)
      .then((drafts) => {
        if (current) setExisting(drafts)
      })
      .catch(() => {
        if (current) setExisting([])
      })

    return () => {
      current = false
    }
  }, [alertId])

  const draft = useCallback(async () => {
    setBusy(true)
    setReading(null)
    try {
      const answer = await alerts.draftPolicy(alertId)
      setReading(readDraft(answer))
      if (answer.saved) setExisting(await alerts.drafts(alertId).catch(() => existing))
    } catch (error) {
      setReading(
        error instanceof ApiError
          ? readDraftFailure(error.status, error.message)
          : readDraftFailure(null, 'The policy could not be drafted.')
      )
    } finally {
      setBusy(false)
    }
  }, [alertId, existing])

  const offer = draftOffer(roles, availability)
  const history = draftHistory(existing)

  // Nothing to say and nothing to offer. A heading over an empty card is a screen telling somebody
  // about a feature they do not have.
  if (!offer.shown && history.shown.length === 0) return null

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Stop this happening again</h3>

        {offer.shown && (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Asks the assistant for a policy that would catch this case across the fleet. What comes
              back is a <strong>draft</strong>: somebody with the authority to publish reviews it,
              decides what it runs on, and approves it. Nothing here reaches a device.
            </p>

            <button type="button" onClick={() => void draft()} disabled={busy} data-testid="draft-policy">
              {busy ? 'Drafting…' : 'Draft a policy from this case'}
            </button>
          </>
        )}

        {reading && (
          <div
            className={reading.outcome === 'drafted' || reading.outcome === 'drafted-unrecorded' ? 'banner' : 'banner error'}
            role="status"
            data-testid={`draft-${reading.outcome}`}
          >
            {reading.message}
            {reading.policyId && (
              <>
                {' '}
                <Link to={`/policies/${reading.policyId}`}>Open the draft</Link>
              </>
            )}
          </div>
        )}

        {/* The findings, so a nearly-right draft can be fixed in the editor rather than asked for
            again. Withholding them to keep the panel tidy would make the second ask as likely to fail
            as the first. */}
        {reading?.findings.length ? (
          <ul className="findings" data-testid="draft-findings">
            {reading.findings.map((finding, index) => (
              <li key={`${finding.fieldPath}-${index}`}>
                <code>{finding.fieldPath}</code> {finding.message}
              </li>
            ))}
          </ul>
        ) : null}

        {/*
          The newest few and a count of the rest, rather than all of them.

          Written after watching this panel with seventeen entries on it. Two analysts working the
          same case produce the same picture, and so does anyone testing against it. What the panel is
          asked is "has somebody already done this", and past the first few rows the answer is the
          count rather than the rows.
        */}
        {history.shown.length > 0 && (
          <>
            <h4>Already drafted from this case</h4>
            <ul className="draft-list" data-testid="case-drafts">
              {history.shown.map((entry) => (
                <li key={`${entry.policyId}-${entry.revision}`}>
                  <Link to={`/policies/${entry.policyId}`}>{describeDraft(entry, formatUtc(entry.draftedUtc))}</Link>
                </li>
              ))}
            </ul>
            {history.more && (
              <p className="muted" data-testid="case-drafts-more">
                {history.more}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
